import { Heart, MessageCircle, Trash2 } from 'lucide-react';
import type { KeyboardEvent, MouseEvent } from 'react';
import DefaultUser from '@/assets/store/icon/user-photo.svg';
import { cn } from '@/lib/utils';
import ImageGallery from '@/shared/components/ui/ImageGallery/ImageGallery';

export interface DonationUser {
  id?: string;
  name: string;
  avatar?: string;
}

export interface DonationMedia {
  url: string;
  /** Pixel size when known, so the photo box has the right shape before it loads */
  width?: number | null;
  height?: number | null;
}

export interface DonationComment {
  id: string;
  user: DonationUser;
  content: string;
  media?: DonationMedia[];
  createdAt: string | Date;
  likes: number;
  isLiked?: boolean;
  /** false hides the delete button even when a delete handler is given */
  canDelete?: boolean;
}

export interface DonationPost extends DonationComment {
  comments: DonationComment[];
  /** Total comments when `comments` is not loaded yet */
  commentCount?: number;
}

interface CardDonationPostProps {
  post: DonationPost;
  className?: string;
  showComments?: boolean;
  showMedia?: boolean;
  /** Shown in the comment section instead of the comments (loading, error) */
  commentsNote?: string;
  /** Makes the card itself tappable (its buttons keep their own actions) */
  onOpen?: (post: DonationPost) => void;
  onLike?: (post: DonationPost) => void;
  onReply?: (post: DonationPost) => void;
  onDelete?: (post: DonationPost) => void;
  /** Makes the comment count a button that opens and closes the comment section */
  onToggleComments?: (post: DonationPost) => void;
  onLikeComment?: (comment: DonationComment, post: DonationPost) => void;
  onDeleteComment?: (comment: DonationComment, post: DonationPost) => void;
}

const dateTimeFormat = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Asia/Jakarta',
});

/** dd/mm/yy - HH:MM in Jakarta time */
const formatDateTime = (value: string | Date) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    dateTimeFormat.formatToParts(date).find((p) => p.type === type)?.value ?? '';
  return `${part('day')}/${part('month')}/${part('year')} - ${part('hour')}:${part('minute')}`;
};

const Avatar = ({ user }: { user: DonationUser }) => (
  <figure className="w-10 h-10 shrink-0 rounded-full overflow-hidden bg-bg-grey-100">
    <img
      src={user.avatar || DefaultUser}
      alt={user.name}
      referrerPolicy="no-referrer"
      onError={(e) => {
        if (!e.currentTarget.src.endsWith(DefaultUser)) e.currentTarget.src = DefaultUser;
      }}
      className="w-full h-full object-cover"
      loading="lazy"
    />
  </figure>
);

interface MediaGalleryProps {
  media: DonationMedia[];
  ownerName: string;
}

/** Product detail gallery without thumbnails; each photo keeps its own ratio (never cropped), up to 340px high. */
const MediaGallery = ({ media, ownerName }: MediaGalleryProps) => (
  <ImageGallery
    images={media.map((item, index) => ({
      url: item.url,
      type: 'image',
      alt: `Media ${index + 1} dari ${ownerName}`,
      width: item.width,
      height: item.height,
    }))}
    adaptiveHeight
    showArrows={false}
    showCounter={media.length > 1}
    showThumbnails={false}
    className="mt-1"
  />
);

interface PostBodyProps {
  item: DonationComment;
  commentCount?: number;
  commentsOpen?: boolean;
  onLike?: () => void;
  onReply?: () => void;
  onDelete?: () => void;
  onToggleComments?: () => void;
  showMedia?: boolean;
}

const PostBody = ({
  item,
  commentCount,
  commentsOpen,
  onLike,
  onReply,
  onDelete,
  onToggleComments,
  showMedia = false,
}: PostBodyProps) => (
  <div className="flex gap-3">
    <Avatar user={item.user} />

    <div className="min-w-0 flex-1 flex flex-col gap-1">
      <div className="flex items-start justify-between gap-2">
        <p className="title2medium text-black truncate">{item.user.name}</p>
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            aria-label={`Hapus postingan ${item.user.name}`}
            className="shrink-0 text-text-danger-500 hover:opacity-80"
          >
            <Trash2 size={18} />
          </button>
        )}
      </div>

      {item.content && (
        <p className="body1 text-black whitespace-pre-line break-words">{item.content}</p>
      )}
      {showMedia && item.media && item.media.length > 0 && (
        <MediaGallery media={item.media} ownerName={item.user.name} />
      )}
      <span className="reg12 text-text-grey">{formatDateTime(item.createdAt)}</span>

      <div className="flex items-center justify-between gap-3 mt-1">
        <div className="inline-flex items-center gap-3 text-text-grey">
          <button
            type="button"
            onClick={onLike}
            aria-pressed={item.isLiked}
            aria-label="Suka"
            className={cn(
              'inline-flex items-center gap-1 reg12',
              item.isLiked && 'text-text-danger-500'
            )}
          >
            <Heart size={16} fill={item.isLiked ? 'currentColor' : 'none'} />
            <span>{item.likes}</span>
          </button>

          {commentCount !== undefined &&
            (onToggleComments ? (
              <button
                type="button"
                onClick={onToggleComments}
                aria-expanded={commentsOpen}
                aria-label="Komentar"
                className="inline-flex items-center gap-1 reg12"
              >
                <MessageCircle size={16} />
                <span>{commentCount}</span>
              </button>
            ) : (
              <span className="inline-flex items-center gap-1 reg12">
                <MessageCircle size={16} />
                <span>{commentCount}</span>
              </span>
            ))}
        </div>

        {onReply && (
          <button type="button" onClick={onReply} className="title3bold text-text-blue-100">
            Balas
          </button>
        )}
      </div>
    </div>
  </div>
);

/** True when the tap landed on a button or link inside the card */
const isOwnControl = (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) =>
  event.target instanceof Element && event.target.closest('button, a') !== null;

const CardDonationPost = ({
  post,
  className,
  showComments = true,
  showMedia = false,
  commentsNote,
  onOpen,
  onLike,
  onReply,
  onDelete,
  onToggleComments,
  onLikeComment,
  onDeleteComment,
}: CardDonationPostProps) => {
  const hasComments = showComments && (post.comments.length > 0 || Boolean(commentsNote));

  return (
    <article
      className={cn(
        'shadowCard w-full p-3 flex flex-col gap-3',
        onOpen && 'cursor-pointer',
        className
      )}
      role={onOpen ? 'link' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? `Buka postingan ${post.user.name}` : undefined}
      onClick={onOpen ? (event) => !isOwnControl(event) && onOpen(post) : undefined}
      onKeyDown={
        onOpen
          ? (event) => {
              if (event.key === 'Enter' && !isOwnControl(event)) onOpen(post);
            }
          : undefined
      }
    >
      <PostBody
        item={post}
        showMedia={showMedia}
        commentCount={post.commentCount ?? post.comments.length}
        commentsOpen={showComments}
        onLike={onLike ? () => onLike(post) : undefined}
        onReply={onReply ? () => onReply(post) : undefined}
        onDelete={onDelete ? () => onDelete(post) : undefined}
        onToggleComments={onToggleComments ? () => onToggleComments(post) : undefined}
      />

      {hasComments && (
        <div className="flex flex-col gap-3 border-t border-blue-cs-200 pt-3 px-4">
          {commentsNote ? (
            <p className="reg12 text-text-grey text-center">{commentsNote}</p>
          ) : (
            post.comments.map((comment, index) => (
              <div key={comment.id} className={cn(index > 0 && 'border-t border-blue-cs-200 pt-3')}>
                <PostBody
                  item={comment}
                  showMedia={showMedia}
                  onLike={onLikeComment ? () => onLikeComment(comment, post) : undefined}
                  onDelete={
                    onDeleteComment && comment.canDelete !== false
                      ? () => onDeleteComment(comment, post)
                      : undefined
                  }
                />
              </div>
            ))
          )}
        </div>
      )}
    </article>
  );
};

export default CardDonationPost;
